import { UseQueryOptions, useQueryClient } from 'react-query'

import { store } from 'app/state/store'
import {
  approveRequest,
  createBasemap,
  declineRequest,
  deleteBasemap,
  deleteUser,
  enableSupportAccess,
  getAccount,
  getAccountBillingInformation,
  getAccountGovernanceConnections,
  getAccountGovernanceMaps,
  getAccountFromWorkspace,
  getBasemapsSettings,
  getAccountAgentSettingsV0,
  getAccountAgentSettingsV1,
  getCustomBasemapsList,
  getDODatasets,
  getInvitationsInProgress,
  getPalettesSettings,
  getSubscriptions,
  getSupportAccessStatus,
  getUsers,
  getWorkflowsSettings,
  patchAccount,
  postInvitation,
  resendInvitation,
  revokeSupportAccess,
  updateAccountGovernanceConnections,
  updateAccountGovernanceMaps,
  updateBasemapsSettings,
  updateAccountAgentSettings,
  updatePalettesSettings,
  updateUserRole,
  updateWorkflowsSettings,
  withdrawInvitation
} from 'features/account/api/account'
import { deleteSubscription } from 'features/data-observatory/api/dataObservatory'
import { Basemap } from 'features/maps/types'
import useNotifications from 'features/notifications/ui/useNotifications'
import { ApiError, RequestStatus } from '../../common'
import { setStatsStatus, updateAccountData } from '../state/accountSlice'
import {
  Account,
  AccountBillingInformation,
  BasemapSettings,
  AccountAgentSettings,
  CatalogDatasetsResponse,
  PalettesSettings,
  Users,
  WorkflowsSettings
} from '../types'

const ACCOUNTS_ROOT_QUERY_KEY = 'accountData'
const MAX_RETRIES = 3

export const accountKeys = {
  accountData: ['accountData'] as const,
  users: ['users'] as const,
  invitations: ['invitations'] as const,
  basemaps: [ACCOUNTS_ROOT_QUERY_KEY, 'basemaps'] as const,
  basemapsSettings: [ACCOUNTS_ROOT_QUERY_KEY, 'basemapSettings'] as const,
  workflowsSettings: [ACCOUNTS_ROOT_QUERY_KEY, 'workflowsSettings'] as const,
  agentSettingsV0: [ACCOUNTS_ROOT_QUERY_KEY, 'agentSettingsV0'] as const,
  agentSettingsV1: [ACCOUNTS_ROOT_QUERY_KEY, 'agentSettingsV1'] as const,
  supportAccess: [ACCOUNTS_ROOT_QUERY_KEY, 'supportAccess'] as const,
  palettesSettings: [ACCOUNTS_ROOT_QUERY_KEY, 'palettesSettings'] as const,
  subscriptions: ['subscriptions'] as const,
  billingInformation: [ACCOUNTS_ROOT_QUERY_KEY, 'billingInformation'] as const,
  datasets: ['datasets'] as const,
  accountGovernanceMaps: [ACCOUNTS_ROOT_QUERY_KEY, 'accountGovernanceMaps'] as const,
  accountGovernanceConnections: [ACCOUNTS_ROOT_QUERY_KEY, 'accountGovernanceConnections'] as const
}

export const accountDataQuery: UseQueryOptions<Account, Error> = {
  queryKey: accountKeys.accountData,
  queryFn: async () => {
    // We always try to get the account from the Accounts API first and if it fails, we fallback to the Workspace API
    try {
      return await getAccount()
    } catch (error) {
      return await getAccountFromWorkspace()
    }
  },
  staleTime: Infinity
}

export const accountBasemapsSettingsQuery: UseQueryOptions<BasemapSettings, Error> = {
  queryKey: accountKeys.basemapsSettings,
  queryFn: () => getBasemapsSettings()
}

export const accountPalettesSettingsQuery: UseQueryOptions<PalettesSettings, Error> = {
  queryKey: accountKeys.palettesSettings,
  queryFn: () => getPalettesSettings()
}

export const accountWorkflowsSettingsQuery: UseQueryOptions<WorkflowsSettings, Error> = {
  queryKey: accountKeys.workflowsSettings,
  queryFn: () => getWorkflowsSettings()
}

export const accountAgentSettingsQueryV0: UseQueryOptions<AccountAgentSettings, Error> = {
  queryKey: accountKeys.agentSettingsV0,
  queryFn: () => getAccountAgentSettingsV0()
}

export const accountAgentSettingsQueryV1: UseQueryOptions<AccountAgentSettings, Error> = {
  queryKey: accountKeys.agentSettingsV1,
  queryFn: () => getAccountAgentSettingsV1()
}

export const accountCustomBasemapsQuery: UseQueryOptions<Basemap[], Error> = {
  queryKey: accountKeys.basemaps,
  queryFn: () => getCustomBasemapsList()
}

export const accountBillingInformationQuery: UseQueryOptions<AccountBillingInformation, Error> = {
  queryKey: accountKeys.billingInformation,
  queryFn: () => getAccountBillingInformation()
}

export const accountUsersQuery: UseQueryOptions<Users[], ApiError> = {
  queryKey: accountKeys.users,
  queryFn: () => getUsers(),
  retry: (failureCount, error) => {
    if (error && error.status === 404) {
      return false // do not retry if no invitations in progress
    }
    return failureCount <= MAX_RETRIES
  },
  staleTime: Infinity
}

export function useDeleteUserMutation({
  userId,
  resourcesReceiverUserId
}: {
  userId: string
  resourcesReceiverUserId?: string
}) {
  const queryClient = useQueryClient()
  const { sendSuccessNotificationLocalize, sendErrorNotificationLocalize } = useNotifications()

  return {
    mutationFn: () => deleteUser({ userId, resourcesReceiverUserId }),
    onSuccess: () => {
      queryClient.setQueryData<Users[]>(accountKeys.users, (users) =>
        (users as Users[]).filter((user: Users) => {
          return user.user_id !== userId
        })
      )
      sendSuccessNotificationLocalize('account.cards.users.deleteUsersDialog.successMessage')
    },
    onError: () => sendErrorNotificationLocalize('account.cards.users.deleteUsersDialog.errorMessage')
  }
}

function useAccountQueries() {
  const queryClient = useQueryClient()
  const { sendSuccessNotificationLocalize, sendErrorNotificationLocalize } = useNotifications()
  const queries = {
    getAccountData: accountDataQuery,
    patchAccount: {
      mutationFn: patchAccount,
      onSuccess: (data) => {
        store.dispatch(updateAccountData(data))
      }
    },
    updateUserRole: {
      mutationFn: updateUserRole,
      onSuccess: (_, variables) => {
        store.dispatch(setStatsStatus(RequestStatus.Idle))

        queryClient.setQueryData<Users[]>(accountKeys.users, (users) =>
          (users as Users[]).map((user): Users => {
            if (user.user_id === variables.userId) {
              return {
                ...user,
                app_metadata: {
                  ...user.app_metadata,
                  roles: [variables.role]
                }
              }
            }

            return user
          })
        )
        sendSuccessNotificationLocalize('account.cards.users.makeViewerDialog.successMessage')
      },
      onError: () => sendErrorNotificationLocalize('account.cards.users.makeViewerDialog.errorMessage')
    },
    getUsers: accountUsersQuery,
    getInvitationsInProgress: {
      queryKey: accountKeys.invitations,
      queryFn: getInvitationsInProgress,
      retry: (failureCount, error) => {
        if (error && error.status === 404) {
          return false // do not retry if no invitations in progress
        }
        return failureCount <= MAX_RETRIES
      }
    },
    sendInvitation: {
      mutationFn: postInvitation,
      onSuccess: (invitations) => {
        if (invitations.some((i) => i.code !== 200)) {
          throw { emails: invitations.map((i) => i.destination).join(',') }
        }
        queryClient.invalidateQueries(accountKeys.invitations, { exact: true })
      }
    },
    resendInvitation: {
      mutationFn: resendInvitation
    },
    withdrawInvitation: {
      mutationFn: withdrawInvitation,
      onSuccess: () => {
        queryClient.invalidateQueries(accountKeys.invitations, { exact: true })
      }
    },
    approveRequest: {
      mutationFn: approveRequest,
      onSuccess: () => {
        queryClient.invalidateQueries(accountKeys.invitations, { exact: true })
      }
    },
    declineRequest: {
      mutationFn: declineRequest,
      onSuccess: () => {
        queryClient.invalidateQueries(accountKeys.invitations, { exact: true })
      }
    },
    getBasemapsSettings: accountBasemapsSettingsQuery,
    updateBasemapsSettings: {
      mutationFn: updateBasemapsSettings,
      onSuccess: (data) => {
        queryClient.setQueryData(accountKeys.basemapsSettings, data)
      }
    },
    getCustomBasemapsList: accountCustomBasemapsQuery,
    deleteBasemap: {
      mutationFn: deleteBasemap,
      onSuccess: () => {
        queryClient.invalidateQueries(accountKeys.basemaps)
      }
    },
    createBasemap: {
      mutationFn: createBasemap,
      onSuccess: () => {
        queryClient.invalidateQueries(accountKeys.basemaps)
      }
    },
    getPalettesSettings: accountPalettesSettingsQuery,
    updatePalettesSettings: {
      mutationFn: updatePalettesSettings,
      onSuccess: (data) => {
        queryClient.setQueryData(accountKeys.palettesSettings, data)
      }
    },
    getWorkflowsSettings: accountWorkflowsSettingsQuery,
    updateWorkflowsSettings: {
      mutationFn: updateWorkflowsSettings,
      staleTime: Infinity,
      cacheTime: Infinity,
      onSuccess: (data) => {
        queryClient.setQueryData(accountKeys.workflowsSettings, data)
      }
    },
    getAccountAgentSettingsV0: accountAgentSettingsQueryV0,
    getAccountAgentSettingsV1: accountAgentSettingsQueryV1,
    updateAccountAgentSettings: {
      mutationFn: updateAccountAgentSettings,
      onSuccess: (data) => {
        queryClient.setQueryData(accountKeys.agentSettingsV0, data)
      }
    },
    getSupportAccessStatus: (accountId) => ({
      queryKey: accountKeys.supportAccess,
      queryFn: () => getSupportAccessStatus({ accountId }),
      retry: (failureCount, error) => {
        if (error && error.status === 404) {
          queryClient.setQueryData(accountKeys.supportAccess, null)
          return false // do not retry if support access is not enabled
        }
        return failureCount <= MAX_RETRIES
      }
    }),
    enableSupportAccess: {
      mutationFn: enableSupportAccess,
      onSuccess: (data) => {
        queryClient.setQueryData(accountKeys.supportAccess, data)
      }
    },
    revokeSupportAccess: {
      mutationFn: revokeSupportAccess,
      onSuccess: () => {
        queryClient.setQueryData(accountKeys.supportAccess, null)
      }
    },
    getSubscriptionsList: {
      queryKey: accountKeys.subscriptions,
      queryFn: getSubscriptions
    },
    deleteSubscription: {
      mutationFn: deleteSubscription,
      onSuccess: (data, variables) => {
        queryClient.setQueryData(accountKeys.datasets, (oldData?: CatalogDatasetsResponse) => {
          return {
            results: oldData?.results.filter((dataset) => dataset.id !== variables) || [],
            totalResults: oldData?.totalResults ? oldData?.totalResults - 1 : 0
          }
        })
        queryClient.invalidateQueries(accountKeys.subscriptions)
      }
    },
    getDODatasets: (subscriptions: string[]) => ({
      queryKey: accountKeys.datasets,
      queryFn: () => getDODatasets(subscriptions),
      enabled: subscriptions && subscriptions.length > 0
    }),
    getAccountGovernanceConnections: {
      queryKey: accountKeys.accountGovernanceConnections,
      queryFn: () => getAccountGovernanceConnections()
    },
    updateAccountGovernanceConnections: {
      mutationFn: updateAccountGovernanceConnections,
      onSuccess: (data) => {
        queryClient.setQueryData(accountKeys.accountGovernanceConnections, data)
      }
    },
    getAccountGovernanceMaps: {
      queryKey: accountKeys.accountGovernanceMaps,
      queryFn: () => getAccountGovernanceMaps()
    },
    updateAccountGovernanceMaps: {
      mutationFn: updateAccountGovernanceMaps,
      onSuccess: (data) => {
        queryClient.setQueryData(accountKeys.accountGovernanceMaps, data)
      }
    }
  }

  return { queries }
}

export default useAccountQueries
